import { useEffect, useState, type FormEvent } from "react";
import { api, type Monitor } from "../api";
import { useProjects } from "../project-context";
import { relTime } from "../time";

export function Uptime() {
  const { selected, loading: projLoading } = useProjects();
  const [rows, setRows] = useState<Monitor[]>([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("https://");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function reload() {
    if (!selected) return;
    setLoading(true);
    api.monitors(selected.id).then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  }
  useEffect(reload, [selected]);

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!selected) return;
    setErr(null);
    setBusy(true);
    try {
      await api.createMonitor(selected.id, { name: name.trim(), url: url.trim() });
      setName("");
      setUrl("https://");
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Kunne ikke opprette");
    } finally {
      setBusy(false);
    }
  }
  async function remove(m: Monitor) {
    if (!confirm(`Slette monitor «${m.name}»?`)) return;
    await api.deleteMonitor(m.id);
    reload();
  }

  if (projLoading) return <div className="spin" />;
  if (!selected) return <div className="card reveal" style={{ marginTop: 40 }}><div className="empty">ingen prosjekt valgt</div></div>;

  return (
    <div>
      <div className="page-head">
        <div>
          <span className="eyebrow">{selected.name}</span>
          <h1 className="page-title">Oppetid</h1>
        </div>
      </div>

      <div className="card reveal" style={{ marginBottom: 16 }}>
        <h3 className="card-title">Ny monitor</h3>
        {err && <div className="error-msg">{err}</div>}
        <form className="row" onSubmit={create} style={{ alignItems: "flex-end" }}>
          <div className="field" style={{ width: 200, marginBottom: 0 }}>
            <label>Navn</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Forside" required />
          </div>
          <div className="field" style={{ flex: 1, marginBottom: 0 }}>
            <label>URL</label>
            <input className="input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://betaworks.no" required />
          </div>
          <button className="btn btn-primary" disabled={busy}>{busy ? "…" : "Legg til"}</button>
        </form>
      </div>

      {loading ? (
        <div className="spin" />
      ) : rows.length === 0 ? (
        <div className="card reveal"><div className="empty">ingen monitorer ennå</div></div>
      ) : (
        rows.map((m) => {
          const state = m.last_ok == null ? "unknown" : m.last_ok ? "up" : "down";
          return (
            <div className="monitor-row reveal" key={m.id}>
              <span className={`status-dot ${state}`} title={state} />
              <div style={{ minWidth: 0 }}>
                <div className="monitor-name">{m.name}</div>
                <div className="monitor-url">{m.url}</div>
              </div>
              <div className="metric">
                <div className="big" style={{ color: state === "down" ? "var(--coral)" : "var(--lime)" }}>
                  {m.uptime_24h != null ? `${m.uptime_24h}%` : "—"}
                </div>
                <div className="lab">Oppetid 24t</div>
              </div>
              <div className="metric">
                <div className="big">{m.avg_ms != null ? `${m.avg_ms}ms` : "—"}</div>
                <div className="lab">{m.last_ts ? `sjekket ${relTime(m.last_ts)}` : "venter"}</div>
              </div>
              <button className="x-btn" onClick={() => remove(m)} title="Slett">✕</button>
            </div>
          );
        })
      )}
      <p className="footer-note">Worker pinger hver monitor på sitt intervall (min. 60 s).</p>
    </div>
  );
}
